import React, { useEffect, useRef, useState } from 'react';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { Heading } from '@components/news/Heading';
import { NewsAndStoriesFilter } from './components/NewsAndStoriesFilter';
import {
  useFetchNewsAndStoriesPageDataQuery,
  useLazyFetchNewsAndStoriesQuery,
} from './NewsAndStories.service';
import { NewsAndStoriesList } from './components/NewsAndStoriesList';
import { NewsAndStoriesFilter as NewsAndStoriesFilterType } from './NewsAndStories.types';
import { useNewsAndStoriesFilter, useNewsAndStoriesPagination } from './NewsAndStories.hooks';
import { ShowMoreBtn } from './components/ShowMoreBtn';
import { NewsAndStoriesToolbar } from './components/NewsAndStoriesToolbar';
import { Tag } from '@components/Tag';
import './NewsAndStories.scss';
import { interpolateString } from '@helpers/stringHelpers';
import {
  getFiltersFromURL,
  setURLfromFilters,
  shouldDefaultOpenFilter,
} from './NewsAndStories.helpers';
import { useUpdateEffect } from '@hooks/useUpdateEffect';

export const NewsAndStoriesPage = () => {
  const defaultFilter = useRef(getFiltersFromURL());
  const { data: pageData, isLoading: isLoadingPage } = useFetchNewsAndStoriesPageDataQuery();

  const [isLoadingNewPage, setIsLoadingNewPage] = useState(false);
  const [doFetchNewsAndStories, { data: newsAndStories, isFetching: isLoadingNewsAndStories }] =
    useLazyFetchNewsAndStoriesQuery();

  const fetchFilteredNews = (filter: NewsAndStoriesFilterType, pageSize: number, page: number) => {
    return doFetchNewsAndStories({
      keyword: filter.searchString,
      country: filter.country?.Id,
      year: filter.year,
      type: filter.category?.Id === 'All' ? undefined : filter.category?.Id,
      industry: filter.industry?.Id,
      page,
      pageSize,
    });
  };

  const { page, onNextPage, resetPage } = useNewsAndStoriesPagination();

  const { filter, resetCount, isFilterDirty, selectorTitles, onResetFilter, setFilterValue } =
    useNewsAndStoriesFilter(defaultFilter.current);

  useEffect(() => {
    // restricting fetch requests if pageSize is not loadded yet
    if (!pageData) return;
    fetchFilteredNews(filter, pageData.PageSize, 1);
  }, [filter, pageData]);

  useUpdateEffect(() => {
    setURLfromFilters(filter);
  }, [filter]);

  useEffect(() => {
    if (!pageData) return;
    setIsLoadingNewPage(true);

    fetchFilteredNews(filter, pageData.PageSize, page)
      .unwrap()
      .then(() => {
        setIsLoadingNewPage(false);
      });
  }, [page]);

  const isInitialLoading = isLoadingPage;

  return (
    <>
      <div className='l-section l-section-100 news-and-stories-page-wrapper'>
        <div className='l-content is-bound'>
          {isInitialLoading && <LoadingSpinner />}
          {pageData && !isInitialLoading && (
            <>
              <Heading headline={pageData.Title} intro={pageData.Description} />
              <NewsAndStoriesFilter
                i18n={pageData.I18n}
                categories={pageData.CategoryList}
                countries={newsAndStories?.Countries}
                industries={newsAndStories?.Industries}
                years={newsAndStories?.Year || pageData.YearsList}
                selectorTitles={selectorTitles}
                filter={filter}
                resetCount={resetCount}
                isDefaultOpen={shouldDefaultOpenFilter(defaultFilter.current)}
                onChange={setFilterValue}
              />
              {newsAndStories && (
                <>
                  <NewsAndStoriesToolbar
                    itemsCount={newsAndStories.TotalResults}
                    itemsText={pageData.I18n.Results}
                  >
                    {isFilterDirty && (
                      <Tag
                        variant='primary'
                        label={pageData.I18n.ResetFilters}
                        onCloseClick={() => {
                          resetPage();
                          onResetFilter();
                        }}
                      />
                    )}
                  </NewsAndStoriesToolbar>
                  {isLoadingNewsAndStories && !isLoadingNewPage ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {newsAndStories.Items.length === 0 ? (
                        <div className='news-and-stories-not-found'>
                          <p className='font-light-md-lh-150'>{pageData.I18n.NoResultFound}</p>
                        </div>
                      ) : (
                        <>
                          <NewsAndStoriesList
                            items={newsAndStories.Items}
                            topics={{
                              position: pageData.PositionofTopics,
                              title: pageData.I18n.TitleofTopics,
                              items: newsAndStories.Topics,
                            }}
                          />
                          <ShowMoreBtn
                            text={pageData.I18n.ShowMore}
                            onClick={onNextPage}
                            showBtn={!isLoadingNewsAndStories && newsAndStories.HasNextPage}
                            showingCountText={
                              !isLoadingNewPage && !isLoadingNewsAndStories
                                ? interpolateString(
                                    pageData.I18n.NewsListPageShowMore,
                                    newsAndStories.ItemsCount.toString(),
                                    newsAndStories.TotalResults.toString()
                                  )
                                : ''
                            }
                          />
                        </>
                      )}
                    </>
                  )}

                  {isLoadingNewPage && <LoadingSpinner />}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NewsAndStoriesPage;
